// Doctor/admin-editable list of clinical guidelines whose year + notes are
// injected verbatim into the Gemini 3 Pro system prompt. Keeping this list
// current (ADA 2027, KDIGO 2025, etc.) ensures every AI citation stays
// aligned with the latest published standards without a code deploy.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, RotateCcw, Trash2, BookOpen, AlertCircle, LinkIcon, ExternalLink } from "lucide-react";
import { CLINIC, LogoTile } from "../lib/clinic";

const emptyRow = () => ({ name: "", full: "", year: new Date().getFullYear(), notes: "", active: true, url: "" });

export default function GuidelineSettings() {
  const [items, setItems] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/guidelines");
      setItems(r.data.guidelines || []);
      setDefaults(r.data.defaults || []);
    } catch (e) {
      toast.error("Failed to load guidelines");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upd = (i, k, v) => setItems(items.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const del = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () => setItems([...items, emptyRow()]);
  const resetToDefaults = () => {
    if (window.confirm("Replace the list with the built-in defaults? Any custom entries will be lost.")) {
      setItems(defaults.map((d) => ({ ...d })));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Filter blanks
      const cleaned = items.filter((x) => x.name && x.year);
      await api.put("/settings/guidelines", { guidelines: cleaned });
      toast.success(`Saved ${cleaned.length} guideline${cleaned.length !== 1 ? "s" : ""}`);
      setItems(cleaned);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const activeCount = items.filter((x) => x.active !== false).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-3">
        <Link to="/dashboard" className="p-1.5 hover:bg-slate-100 rounded" data-testid="settings-back">
          <ArrowLeft size={16} />
        </Link>
        <LogoTile size={32} />
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-900">Guideline Refresh</div>
          <div className="text-[11px] text-slate-500">{CLINIC.short} · edit the year/section text used in every AI citation</div>
        </div>
        <Button variant="outline" size="sm" onClick={resetToDefaults} className="gap-1.5" data-testid="settings-reset">
          <RotateCcw size={12} /> Reset to defaults
        </Button>
        <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white gap-1.5" data-testid="settings-save">
          <Save size={13} /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-4">
        <div className="text-xs bg-sky-50 border border-sky-200 rounded-md p-3 text-sky-900 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>
            <strong>How this is used:</strong> Every time you tap <em>Ask Gemini</em> in Clinical Guidance, the list below is embedded verbatim into the system prompt. Bumping ADA to 2027 or adding new sections here changes every citation instantly — no redeploy. Toggle a row off (right switch) to remove it from the prompt without deleting it. Add a <strong>Reference URL</strong> (PDF or webpage) so any AI citation naming this guideline becomes a clickable link in the Guidance dialog.
          </div>
        </div>

        <div className="flex items-center justify-between text-xs">
          <div className="text-slate-600"><BookOpen size={12} className="inline mr-1" /> <strong>{activeCount}</strong> active of {items.length} · citations feed Gemini 3 Pro</div>
          <Button variant="outline" size="sm" onClick={add} className="gap-1 h-7 text-[11px]" data-testid="settings-add">
            <Plus size={11} /> Add guideline
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="p-3 w-24">Short</th>
                <th className="p-3">Full name</th>
                <th className="p-3 w-20">Year</th>
                <th className="p-3">Notes / key sections</th>
                <th className="p-3">Reference URL (PDF / web)</th>
                <th className="p-3 w-20">Active</th>
                <th className="p-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500 text-xs">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500 text-xs">No guidelines. Tap "Add guideline" or "Reset to defaults".</td></tr>
              )}
              {items.map((g, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`settings-row-${i}`}>
                  <td className="p-2"><Input value={g.name || ""} onChange={(e) => upd(i, "name", e.target.value)} placeholder="ADA" className="h-8 text-sm" data-testid={`settings-name-${i}`} /></td>
                  <td className="p-2"><Input value={g.full || ""} onChange={(e) => upd(i, "full", e.target.value)} placeholder="American Diabetes Association Standards of Care" className="h-8 text-sm" /></td>
                  <td className="p-2"><Input type="number" value={g.year || ""} onChange={(e) => upd(i, "year", parseInt(e.target.value) || null)} placeholder="2026" className="h-8 text-sm tabular w-20" data-testid={`settings-year-${i}`} /></td>
                  <td className="p-2"><Input value={g.notes || ""} onChange={(e) => upd(i, "notes", e.target.value)} placeholder="§11 pharmacotherapy; §10 lipids" className="h-8 text-sm" /></td>
                  <td className="p-2">
                    <div className="flex items-center gap-1.5">
                      <LinkIcon size={12} className="text-slate-400 shrink-0" />
                      <Input
                        value={g.url || ""}
                        onChange={(e) => upd(i, "url", e.target.value)}
                        placeholder="https://diabetesjournals.org/care/…"
                        className="h-8 text-sm flex-1"
                        data-testid={`settings-url-${i}`}
                      />
                      {g.url && (
                        <a
                          href={g.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 text-pink-600 hover:bg-pink-50 rounded shrink-0"
                          title="Open reference in new tab"
                          data-testid={`settings-url-open-${i}`}
                        >
                          <ExternalLink size={13} />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="p-2"><Switch checked={g.active !== false} onCheckedChange={(v) => upd(i, "active", v)} data-testid={`settings-active-${i}`} /></td>
                  <td className="p-2 text-right"><button onClick={() => del(i)} className="p-1 text-red-600 hover:bg-red-50 rounded" data-testid={`settings-del-${i}`}><Trash2 size={13} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] text-slate-500 mt-4">
          Suggested update cadence: check ADA (Dec), KDIGO (annual), ESC (Aug/Sep), AACE (varies), AASLD (annual), RSSDI (annual). Add year 2027 in Dec for ADA, refresh KDIGO after each annual synthesis.
        </div>

        <DrugTablesSection />
      </main>
    </div>
  );
}

// -------- Guideline Drug Tables: pasted tables → live dose chips + CI alerts --------
function DrugTablesSection() {
  const [tables, setTables] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [gname, setGname] = useState("");
  const [raw, setRaw] = useState("");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/guideline-tables").then((r) => setTables(r.data.tables || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const parse = async () => {
    if (!raw.trim()) { toast.error("Paste the table text first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/guideline-tables/parse", { text: raw });
      setRows(r.data.rows || []);
      toast.success(`Parsed ${r.data.rows.length} drugs — review then save`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Parse failed");
    } finally { setBusy(false); }
  };

  const saveTable = async () => {
    if (!gname.trim() || !rows?.length) { toast.error("Guideline name and parsed rows required"); return; }
    try {
      await api.post("/guideline-tables", { guideline: gname.trim(), rows });
      toast.success("Drug table saved — dose chips are now live on prescriptions");
      setShowAdd(false); setGname(""); setRaw(""); setRows(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const delTable = async (t) => {
    if (!window.confirm(`Delete the "${t.guideline}" drug table?`)) return;
    await api.delete(`/guideline-tables/${t.table_id}`);
    load();
  };

  return (
    <div className="mt-8 border-t border-slate-200 pt-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Guideline drug tables</div>
          <div className="text-[11px] text-slate-500 max-w-xl mt-0.5">
            Paste a guideline's drug table (from ChatGPT or the PDF) — AI structures it into Drug / Dose / Contraindications.
            Matching drugs on any prescription then show a live dose chip and contraindication alerts.
          </div>
        </div>
        <Button onClick={() => setShowAdd(!showAdd)} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1" data-testid="gdt-add-btn">
          <Plus size={13} /> Paste new table
        </Button>
      </div>

      {showAdd && (
        <div className="mt-3 border border-indigo-200 bg-indigo-50/40 rounded-md p-4 space-y-3" data-testid="gdt-add-panel">
          <Input value={gname} onChange={(e) => setGname(e.target.value)} placeholder='Guideline name, e.g. "Indian Psychiatric Society Depression CPG 2026"' data-testid="gdt-name-input" />
          <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={6}
            placeholder="Paste the drug table text here (any format — drug, dose, contraindications)…"
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-xs font-mono resize-y" data-testid="gdt-raw-input" />
          <div className="flex items-center gap-2">
            <Button onClick={parse} disabled={busy} size="sm" variant="outline" className="gap-1" data-testid="gdt-parse-btn">
              {busy ? "Parsing…" : "AI Parse"}
            </Button>
            {rows && <Button onClick={saveTable} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="gdt-save-btn">Save {rows.length} drugs</Button>}
          </div>
          {rows && (
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded bg-white">
              <table className="w-full text-[11px]">
                <thead><tr className="text-left text-slate-500 border-b"><th className="p-1.5">Drug</th><th className="p-1.5">Dose</th><th className="p-1.5">Contraindications</th></tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 align-top">
                      <td className="p-1.5 font-medium">{r.drug}</td>
                      <td className="p-1.5 tabular">{r.dose}</td>
                      <td className="p-1.5 text-slate-600">{r.contra}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 space-y-2">
        {tables.map((t) => (
          <div key={t.table_id} className="border border-slate-200 rounded-md bg-white" data-testid={`gdt-${t.table_id}`}>
            <div className="flex items-center justify-between px-3 py-2">
              <button onClick={() => setExpanded(expanded === t.table_id ? null : t.table_id)} className="text-xs font-medium text-slate-800 hover:text-indigo-700 text-left">
                {t.guideline} <span className="text-slate-400 font-normal">· {(t.rows || []).length} drugs</span>
              </button>
              <button onClick={() => delTable(t)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`gdt-del-${t.table_id}`}><Trash2 size={12} /></button>
            </div>
            {expanded === t.table_id && (
              <div className="border-t border-slate-100 max-h-56 overflow-y-auto">
                <table className="w-full text-[11px]">
                  <tbody>
                    {(t.rows || []).map((r, i) => (
                      <tr key={i} className="border-b border-slate-50 align-top">
                        <td className="p-1.5 font-medium w-40">{r.drug}</td>
                        <td className="p-1.5 tabular w-44">{r.dose}</td>
                        <td className="p-1.5 text-slate-600">{r.contra}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
        {tables.length === 0 && <div className="text-[11px] text-slate-400 italic">No drug tables yet.</div>}
      </div>
    </div>
  );
}
